/* Vertical specialization tracks — generated by sales-academy-factory from the
 * Academy spec. The first line of business is the live/active domain; the rest
 * are "configured" placeholders. Tune the prose by hand if you like — this is a
 * starting draft. */
export type VerticalKey =
  | 'residential_buyer_representation'
  | 'residential_seller_listing_and_marketing'
  | 'investment_property_and_multi_family_advisory'
  | 'relocation_and_corporate_housing_services'
  | 'new_construction_and_builder_sales';

/** Wider than persona PaletteRole — verticals pick from the full token set. */
export type VerticalColor = 'platform' | 'coach' | 'win' | 'warmth' | 'growth' | 'danger';

export interface Vertical {
  key: VerticalKey;
  display_name: string;
  short_label: string;
  description: string;
  status: 'active' | 'configured';
  persona_count: number;
  scenario_count: number;
  module_count: number;
  lesson_count: number;
  hooks: string[];
  color_key: VerticalColor;
  initials: string;
}

export const VERTICALS_SEED: Record<VerticalKey, Vertical> = {
  residential_buyer_representation: {
    key: 'residential_buyer_representation',
    display_name: 'Residential Buyer Representation',
    short_label: 'Buyer Rep',
    description:
      'Trains agents on guiding first-time and repeat buyers from initial consultation through closing, covering needs discovery, offer strategy, and objection handling. Scenarios reflect real market conditions including low inventory, multiple-offer situations, and buyer agency agreement conversations.',
    status: 'active',
    persona_count: 14,
    scenario_count: 38,
    module_count: 9,
    lesson_count: 54,
    hooks: ['Master the buyer consultation to win signed agency agreements', 'Handle \'I want to wait for rates to drop\' with confidence', 'Navigate competitive offer scenarios without losing your client', 'Turn open house leads into committed, loyal buyers'],
    color_key: 'growth',
    initials: 'RB',
  },
  residential_seller_listing_and_marketing: {
    key: 'residential_seller_listing_and_marketing',
    display_name: 'Residential Seller Listing & Marketing',
    short_label: 'Listing Sales',
    description:
      'Prepares agents to win listing appointments, defend pricing strategy, and manage seller expectations through every stage of the marketing cycle. Conversations range from pre-listing walkthroughs to price-reduction talks and multiple-offer presentation skills.',
    status: 'configured',
    persona_count: 11,
    scenario_count: 28,
    module_count: 7,
    lesson_count: 41,
    hooks: ['Defend your CMA when sellers cite a higher Zillow estimate', 'Win listings against discounters with a full-service value story', 'Coach sellers through emotional pricing decisions', 'Turn an expired listing cold call into a signed agreement'],
    color_key: 'platform',
    initials: 'RS',
  },
  investment_property_and_multi_family_advisory: {
    key: 'investment_property_and_multi_family_advisory',
    display_name: 'Investment Property & Multi-Family Advisory',
    short_label: 'Investment',
    description:
      'Builds advisor-level fluency for agents working with landlords, house hackers, and portfolio investors evaluating residential income and small multi-family assets. Scenarios cover cap rate conversations, cash-on-cash return analysis, and long-term portfolio growth planning.',
    status: 'configured',
    persona_count: 10,
    scenario_count: 24,
    module_count: 6,
    lesson_count: 36,
    hooks: ['Speak an investor\'s language: cap rates, NOI, and cash flow', 'Position yourself as a strategic advisor, not just a deal finder', 'Handle \'the numbers don\'t pencil\' with creative structuring ideas', 'Prospect absentee owners and equity-rich landlords effectively'],
    color_key: 'coach',
    initials: 'IP',
  },
  relocation_and_corporate_housing_services: {
    key: 'relocation_and_corporate_housing_services',
    display_name: 'Relocation & Corporate Housing Services',
    short_label: 'Relocation',
    description:
      'Equips agents to manage the unique timelines, emotional stakes, and corporate approval layers of transferee and corporate housing clients. Training focuses on quick-trust consultations, remote buying conversations, and coordinating with HR and relocation management companies.',
    status: 'configured',
    persona_count: 9,
    scenario_count: 20,
    module_count: 5,
    lesson_count: 30,
    hooks: ['Build instant rapport with transferees on a tight timeline', 'Navigate corporate approval processes without losing momentum', 'Guide remote buyers to confident decisions without an in-person tour', 'Win referrals from relocation management companies'],
    color_key: 'warmth',
    initials: 'R&',
  },
  new_construction_and_builder_sales: {
    key: 'new_construction_and_builder_sales',
    display_name: 'New Construction & Builder Sales',
    short_label: 'New Build',
    description:
      'Trains on-site sales counselors and buyer\'s agents to navigate the new construction sales process, from model home tours to upgrade selections, contract contingencies, and managing expectations through extended build timelines. Scenarios address co-op agent relationships, build delays, and competitive resale comparisons.',
    status: 'configured',
    persona_count: 10,
    scenario_count: 22,
    module_count: 6,
    lesson_count: 33,
    hooks: ['Turn a model home walk-through into a signed purchase agreement', 'Handle \'why buy new when I can get more house resale?\' objections', 'Manage buyer anxiety through long construction timelines', 'Build co-op agent relationships that drive repeat traffic'],
    color_key: 'win',
    initials: 'NC',
  },
};

export const VERTICAL_ORDER: VerticalKey[] = [
  'residential_buyer_representation',
  'residential_seller_listing_and_marketing',
  'investment_property_and_multi_family_advisory',
  'relocation_and_corporate_housing_services',
  'new_construction_and_builder_sales',
];
